import React, {
    cloneElement,
    forwardRef,
    type HTMLProps,
    isValidElement,
    type RefObject,
    useMemo
} from 'react';

import { useMergeRefs } from '../../hooks/useMergeRefs';
import { isFragment, supportRef } from '../../utils/components';

import { useFloatingContext } from './Floating.context';
import { combineEventHandlers } from './Floating.utils';
import { VisuallyDisabled } from './VisuallyDisabled';

type FloatingTriggerProps = HTMLProps<HTMLElement> & {
    visuallyDisabled?: boolean;
};

export const FloatingTrigger = forwardRef<HTMLElement, FloatingTriggerProps>(
    ({ children, visuallyDisabled, ...props }: FloatingTriggerProps, propRef) => {
        const { refs, open, getReferenceProps } = useFloatingContext();
        const childrenRef =
            isValidElement(children) && 'ref' in children
                ? (children.ref as RefObject<unknown>)
                : undefined;
        const ref = useMergeRefs([refs.setReference, propRef, childrenRef]);
        const child = useMemo(() => {
            if (visuallyDisabled) {
                return <VisuallyDisabled>{children}</VisuallyDisabled>;
            }
            if (!isValidElement(children) || isFragment(children) || !supportRef(children)) {
                // Allow the tooltip to appear even if the child is not a valid element
                // or if the `ref` property points to an invalid DOM element, such as a
                // component with an imperative ref setup.
                return <span>{children}</span>;
            }
            return children;
        }, [children, visuallyDisabled]);
        const childProps = getReferenceProps({
            ref,
            ...props,
            ...child.props,
            ...combineEventHandlers(props, child.props),
            ...({
                'data-state': open ? 'open' : 'closed'
            } as HTMLProps<never>)
        });
        return cloneElement(child, childProps);
    }
);

FloatingTrigger.displayName = 'FloatingTrigger';
